import { Box } from '@modusign/design-system';
import { DropdownOption } from '@modusign/react-signing/types';
import cx from 'classnames';
import { ChangeEvent, MouseEventHandler } from 'react';
import { DeepReadonly } from 'ts-essentials';

import getFontFaceFontFamily from '@app/features/apply-react-signing/components/FieldRenderer/libs/field-font-family';
import { DROPDOWN_PLACE_HOLDER } from '@app/features/apply-react-signing/components/FieldRenderer/Setup/FieldRenderers/ConfiguralbeDropdownField';
import { DropdownFieldSelect } from '@app/features/apply-react-signing/components/FieldSetupStep/DropdownFieldSelect';
import TransformTextStyle from '@app/features/apply-react-signing/components/FieldSetupStep/Placeholders/TextBaseFieldPlaceholder/TransformTextStyle';
import createFieldClassNameByParticipantOrder from '@app/libs/participantFields/createFieldClassNameByParticipantOrder';
import orderRotationForColor from '@app/libs/participantFields/orderRotationForColor';
import PDFFontNormalizer from '@app/libs/reactPdfJs/utils/PDFFontNormalizer';

import './DropdownFieldPreview.scss';

interface DropdownFieldPreviewProps {
  fontPixelSize: number;
  participantOrder: number;
  star: boolean;
  value?: string;
  style: Partial<TransformTextStyle>;
  options: DeepReadonly<DropdownOption[]>;
  invalid?: boolean;
  selectRef?: React.RefObject<HTMLSelectElement>;
  restrictTextClick: boolean;
  onMouseDown: MouseEventHandler<HTMLDivElement>;
  onHoveredOnPreviewButton?: (hovered: boolean) => void;
  onSelectChange?: (e: ChangeEvent<HTMLSelectElement>) => void;
}

const defaultProps = {
  fontPixelSize: 16,
  participantOrder: 1,
  rootElementSize: null,
  star: false,
  style: null,
  invalid: false,
  onMouseDown(_event: MouseEventHandler<HTMLDivElement>) {},
};

let svgID = 0;
// PDFEditor 의 iTEXT 의 lettering space와 DOM의 lettering space가 다릅니다.
// 이를 경험적으로 구한 비율을 통해 fontPixelSize * 비율로 lettering space를 결정합니다.
export const LETTERING_SPACE_EM_VALUE = 0.0008;

function DropdownFieldDropdownPreview({
  fontPixelSize,
  participantOrder,
  value,
  star,
  style,
  options,
  invalid,
  selectRef,
  restrictTextClick,
  onHoveredOnPreviewButton,
  onSelectChange,
  onMouseDown,
}: DropdownFieldPreviewProps) {
  const expandsStyle = { ...style, fontWeight: 400 };
  const adjustedStyle = PDFFontNormalizer.toPixelSizeStyleProps(expandsStyle, 1.0);
  const rotatedOrder = orderRotationForColor(participantOrder);

  if (expandsStyle.fontFamily) {
    adjustedStyle.fontFamily = getFontFaceFontFamily(expandsStyle.fontFamily);
  }
  svgID += 1;

  const dropdownFieldPlaceholderClassByOrder = createFieldClassNameByParticipantOrder(
    'DropdownFieldPreview',
    participantOrder
  );
  return (
    <>
      <Box // eslint-disable-line jsx-a11y/no-static-element-interactions
        className={cx('DropdownFieldPreview', {
          'DropdownFieldPreview-invalid': invalid,
          [dropdownFieldPlaceholderClassByOrder]: !invalid,
        })}
        style={{
          width: '100%',
        }}
        onMouseDown={onMouseDown}
      >
        {star && <div className="DropdownFieldPreview__star">*</div>}

        <div className="DropdownFieldPreview__line-guide">
          <svg height="100%" width="100%">
            <defs>
              <pattern
                height={fontPixelSize}
                id={`DropdownFieldPreview__line-guide-svg-${svgID}`}
                patternUnits="userSpaceOnUse"
                viewBox="0 0 26 26"
                width={fontPixelSize}
              >
                <g fill="none" fillOpacity="0.15" fillRule="evenodd">
                  <rect fill="black" height="1" width="9" x="2" y="24" />
                  <rect fill="black" height="1" width="9" x="14.991" y="24" />
                </g>
              </pattern>
            </defs>
            <rect
              fill={`url(#DropdownFieldPreview__line-guide-svg-${svgID})`}
              height="100%"
              width="100%"
            />
          </svg>
        </div>

        <Box height="100%" overflow="hidden" position="relative" width="100%">
          <DropdownFieldSelect
            buttonHoverStyle={{
              backgroundColor: `participantOrder${rotatedOrder}`,
            }}
            defaultOptionLabel="--선택--"
            placeholder={DROPDOWN_PLACE_HOLDER}
            selectRef={selectRef}
            style={{
              letterSpacing: `${LETTERING_SPACE_EM_VALUE}em`,
            }}
            transformTextStyle={adjustedStyle}
            value={value}
            onChange={onSelectChange}
            onHovered={onHoveredOnPreviewButton}
          >
            {options.map((o, index) => {
              /**
               * 드롭다운 옵션의 값은 사용자 입력이므로 중복될 수 있기에 index와의 조합으로 key를 사용합니다.
               */
              // eslint-disable-next-line react/no-array-index-key
              return <option key={`${o.value}-${index}`}>{o.value}</option>;
            })}
          </DropdownFieldSelect>
        </Box>
      </Box>
      {restrictTextClick && (
        <Box
          height="100%"
          left="0"
          position="absolute"
          top="0"
          width={`calc(100% - ${fontPixelSize}px)`}
        />
      )}
    </>
  );
}

DropdownFieldDropdownPreview.defaultProps = defaultProps;

export default DropdownFieldDropdownPreview;
